import React from 'react'
import { useCurrentUser } from '../../../utils/CurrentUserContext'
import { CompanyType } from '../../../__generated__/graphql'

interface TopStatsProps {
  totalData?: any
}

const TopStats: React.FC<TopStatsProps> = ({ totalData }) => {
  const { user } = useCurrentUser()

  const formatNumber = (num: number) => num ? num.toLocaleString() : '0'
  const formatPercentage = (num: number) => num ? `${num.toFixed(2)}%` : '0%'
  const formatCurrency = (num: number) => ({
    value: <><span className="text-xs text-gray-500">DKK </span>{num ? num.toFixed(2) : '0.00'}</>,
    className: ''
  })
  const formatSeconds = (num: number) => num ? `${num.toFixed(2)}s` : '0s'

  // Main 3 tiles - larger height
  const mainStats = [
    { name: "Total Impressions", total: formatNumber(totalData?.tdImpressions || 0) },
    user.company.type === CompanyType.Publisher
      ? { name: "Total Revenue", total: formatCurrency(totalData?.publisherRevenue || 0) }
      : { name: "Total Cost", total: formatCurrency(totalData?.totalCost || 0) },
    { name: "Total Clicks", total: formatNumber(totalData?.tdClicks || 0) }
  ]

  // Secondary 5 tiles - smaller height
  const secondaryStats = [
    { name: "Reads", total: formatNumber(totalData?.reads || 0) },
    {
      name: "CTR", 
      total: totalData?.tdImpressions > 0 && totalData?.tdClicks
        ? formatPercentage((totalData.tdClicks / totalData.tdImpressions) * 100)
        : '0%'
    },
    { name: "Click Outs", total: formatNumber(totalData?.clickOuts || 0) },
    user.company.type === CompanyType.Publisher
      ? { name: "eCPM", total: formatCurrency(totalData?.eCpm || 0) }
      : {
          name: "eCPM", 
          total: totalData?.tdImpressions > 0 && totalData?.totalCost
            ? formatCurrency(totalData.totalCost / (totalData.tdImpressions / 1000))
            : formatCurrency(0)
        },
    { name: "Avr. Read Time", total: formatSeconds(totalData?.avgReadtime || 0) }
  ]

  return (
    <div className="mb-6 space-y-4">
      {/* Main 3 tiles */}
      <dl className="grid grid-cols-1 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow md:grid-cols-3 md:divide-y-0 md:divide-x">
        {mainStats.map((item, index) => (
          <div key={index} className="px-4 py-4 sm:py-6 sm:px-8">
            <dt className="text-sm font-semibold text-gray-900">{item.name}</dt>
            <dd className="mt-2">
              <div className="flex items-baseline text-3xl font-semibold text-indigo-600">
                {typeof item.total === 'string' ? item.total : item.total.value}
              </div>
            </dd>
          </div>
        ))}
      </dl>

      {/* Secondary 5 tiles */}
      <dl className="grid grid-cols-1 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow md:grid-cols-5 md:divide-y-0 md:divide-x">
        {secondaryStats.map((item, index) => (
          <div key={index} className="px-3 py-2 sm:py-4 sm:px-6">
            <dt className="text-xs font-semibold text-gray-900 uppercase tracking-wide">{item.name}</dt>
            <dd className="mt-1">
              <div className="flex items-baseline text-xl font-semibold text-indigo-600">
                {typeof item.total === 'string' ? item.total : item.total.value}
              </div>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

export default TopStats
